import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@mono/ui-components';
import { useParams } from 'react-router-dom';
import { useFeatureFlag, useFeatureFlagsReady } from '../../../../contexts/featureFlagContext';
import { useMediaQuery } from '../../../../hooks/useMediaQuery';
import { useProposalProject } from '../../../../hooks/useProposalProject';
import { MyWorkPanel } from '../home/MyWorkPanel';
import { ProjectBriefingPanel } from '../home/ProjectBriefingPanel';
import { HomePageRedesign } from './HomePageRedesign';
import { proposalLoadErrorMessage } from './proposalLoadError';
/**
 * V2 Home page — the prod rollout gate.
 *
 * Hybrid landing per the dashboard rewrite plan: split-screen with My Work
 * on the left (workflow lens) and Project Briefing on the right (project
 * lens). On narrow viewports the two panels stack vertically.
 *
 * Both panels pull from the existing query cache shared with the rest of
 * the V2 dashboard, so opening Home is a no-cost operation if the user has
 * already loaded other V2 pages.
 */ export function HomePage() {
    const flagsReady = useFeatureFlagsReady();
    const redesignEnabled = useFeatureFlag('specra_redesign_proposal_home');
    if (flagsReady && redesignEnabled) {
        return /*#__PURE__*/ _jsx(HomePageRedesign, {});
    }
    return /*#__PURE__*/ _jsx(HomePageLegacy, {});
}
function HomePageLegacy() {
    const { id } = useParams();
    const { data, isLoading, error } = useProposalProject(id ?? '');
    const isDesktop = useMediaQuery('(min-width: 1024px)');
    if (!id) return null;
    if (isLoading) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center text-sm text-slate-500",
            children: "Loading project..."
        });
    }
    if (error || !data) {
        return /*#__PURE__*/ _jsx("div", {
            className: "flex h-full items-center justify-center text-sm text-rose-600",
            children: proposalLoadErrorMessage(error)
        });
    }
    const dueDate = data.solicitation?.dueDate;
    // Stacked vertical layout on narrow viewports — main pane scrolls.
    if (!isDesktop) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex h-full flex-col overflow-auto",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "border-b border-slate-200",
                    children: /*#__PURE__*/ _jsx(MyWorkPanel, {
                        projectId: id,
                        dueDate: dueDate
                    })
                }),
                /*#__PURE__*/ _jsx(ProjectBriefingPanel, {
                    projectId: id,
                    projectName: data.project.name,
                    dueDate: dueDate,
                    solicitation: data.solicitation
                })
            ]
        });
    }
    // Desktop split layout.
    return /*#__PURE__*/ _jsxs(ResizablePanelGroup, {
        direction: "horizontal",
        className: "h-full",
        children: [
            /*#__PURE__*/ _jsx(ResizablePanel, {
                defaultSize: 55,
                minSize: 35,
                children: /*#__PURE__*/ _jsx(MyWorkPanel, {
                    projectId: id,
                    dueDate: dueDate
                })
            }),
            /*#__PURE__*/ _jsx(ResizableHandle, {
                withHandle: true
            }),
            /*#__PURE__*/ _jsx(ResizablePanel, {
                defaultSize: 45,
                minSize: 30,
                children: /*#__PURE__*/ _jsx(ProjectBriefingPanel, {
                    projectId: id,
                    projectName: data.project.name,
                    dueDate: dueDate,
                    solicitation: data.solicitation
                })
            })
        ]
    });
}
